/**
 * CandidatesTable — the rows of the Candidates tab, and its headers.
 *
 * Presentational: every filter, the sort and the page live in `CandidatesTab`
 * (and, through it, in the URL). This component renders what it is handed and
 * reports clicks. Sorting is SERVER-side over the whole set, never client-side
 * over the page — the headers do not sort anything themselves; they ask.
 *
 * WHAT CHANGED FROM `AllCandidatesTable` (2026-10-06, epic
 * `2026-10-06-search-candidates-reports`, sub-task 02). The Dashboard's All
 * Candidates table was a flat grid of nine single-value columns. The tab's
 * table follows the Prototype view of the epic artifact instead:
 *
 *   - **Candidate** carries the name with the city and state under it, and the
 *     LinkedIn link at the cell's RIGHT EDGE — the shape People Lists renders
 *     (`import-detail/ContactsTable.tsx`, `LinkedInProfileLink`), reproduced
 *     here by pattern rather than imported: the imports feature is another
 *     bounded context.
 *   - **Title and company** are two lines of one cell: the title qualifies the
 *     person, the company qualifies the title.
 *   - **Stage** is a pill with the stage's colour dot — the SAME palette the
 *     donut and the funnel use (`components/stages/chartPalette.ts`, a pure function of
 *     the stage name, so a stage looks the same everywhere) — and, under a
 *     Rejected pill, the rejection reason. The reason was downloaded and
 *     dropped by the dashboard build until the projection grew the field.
 *   - **In stage** is the days since the current stage began (`stage_since`,
 *     folded from the stage history), the question "how long has this person
 *     been sitting here" that the dashboard never answered.
 *   - **Reports** carries the stored-report downloads (sub-task 04,
 *     `ReportDownloadMenu`): one button per report GROUP — Assessments
 *     (Recruiter, Client), References (Official, Backchannel) — of which the
 *     row's projection flags at least one kind, an em dash for none (epic
 *     `2026-10-22-candidate-reports-four-kinds`).
 *
 * THE STAGE COLUMNS SORT BY THE LADDER AGAIN, and this reverses a decision
 * worth naming. Trello #711 CL3 (2026-08-21) made Stage and Highest stage sort
 * A-Z like every other column, because two controls side by side (ladder and
 * A-Z) read as a bug. The A-Z control is still gone — one header, one ordering
 * — but the one ordering is now the ladder: the tab's default order is
 * furthest-along first (Jose, 2026-10-05), and a header that lit up for a
 * different ordering than the rows were in would be the confusion #711 fixed,
 * arriving from the other side. `stage_alpha` stays accepted on the API and
 * in the URL for old links.
 *
 * THREE THINGS THE MOVE DROPPED CAME BACK (Jose, 2026-10-07, sub-task 05). The
 * first cut followed the prototype's column list and left out what the All
 * Candidates panel had decided with Jared on 2026-08-25/27:
 *
 *   - **Thrive**, second after Candidate: the CONTACT id as a deep link to
 *     `nu.thrivetrm.com/contacts/<id>` — People Lists' cell (`ThriveIdCell`,
 *     shared, rendered here WITHOUT its photo: the row IS the Thrive contact
 *     and its photo sits beside the name). No sort control, the one stated
 *     exception to "every column sorts": an opaque id orders nothing a
 *     recruiter asks. The candidacy id stays hidden — two numeric ids side by
 *     side would be a riddle.
 *   - **Email**, after Title and company: a `mailto:` link, sortable.
 *   - **The company sort**, as the SECOND control of the Title and company
 *     header. Trello #711 CL3 removed a second control from the stage headers
 *     because two arrows for ONE label read as a bug; here each control names
 *     what it orders (Title / company), so the pair reads as two questions.
 *
 * ONE CELL, THREE LINES; ONE STAGE COLUMN (Jose, 2026-10-08, epic
 * `2026-10-08-candidates-polish-thrive-avatar-worker`, sub-task 01). Nine
 * columns in a `min-w-[72rem]` table pushed Reports off a 1280 px screen. The
 * Candidate cell now carries the name, then `Title · Company` on one line, then
 * the email as a `mailto:` link — the Title and company and Email columns are
 * gone, and the city and state with them (the candidate panel and the
 * document still print them). The Stage cell carries the current pill, then
 * `↑ <highest stage>` only when the highest differs, then the rejection
 * reason — the Highest stage column is gone. Sorting follows the columns: the
 * Candidate header keeps the name, title and company controls, Stage sorts by
 * the current stage, and the email and highest-stage sorts went with their
 * columns (`CandidatesTab` reads a stale `?sort=email` as the default). The
 * "Candidates, as shown" DOCUMENT keeps Email and Highest stage as columns of
 * their own: a document has no width problem (`export/ReportDocument.tsx`).
 *
 * The AVATAR sits left of the name (`Avatar size="lg"`, the People Lists
 * shape): initials until a photo resolves, the Thrive photo after, the LinkedIn
 * photo as the middle fallback — `useResolveThriveAvatars` hands the table a
 * contact-id → URL map and the table renders what it is given.
 *
 * SURFACES ARE THE LIST TABLES' (PR #1009 review iter 1): `bg-table-surface`
 * on the wrapper, `bg-table-cell-surface` on the sticky header cells (§8 — an
 * opaque cell over the sheet, not the page's `bg-surface`), `hover:bg-row-hover`
 * on rows, the same three tokens `SearchesTable`, `ImportsTable` and the
 * People Lists `ContactsTable` use. The Dashboard's `AllCandidatesTable` sat
 * inside a `bg-surface` Panel and could lean on it; this one sits on the page.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchCandidateRow, SearchCandidateSortKey } from "@nu-assist/contracts";
import { Avatar } from "@nu-assist/ui";
import { Linkedin, Mail } from "lucide-react";
import type { MouseEvent } from "react";

import type { CandidateReportsClient } from "../../../clients/candidateReports.client";
import { ThriveIdCell } from "../../../components/ThriveIdCell";
import { StagePill } from "../../../components/stages/StagePill";
import { REJECTED_STAGE } from "../../../components/stages/ladder";
import { formatDate, formatDaysInStage } from "../../../lib/format/date";
import { CandidateReportButtons } from "./ReportDownloadMenu";

export interface CandidatesTableProps {
  rows: readonly SearchCandidateRow[];
  sort: SearchCandidateSortKey;
  order: "asc" | "desc";
  /** Ask for a column. Pressing the ACTIVE column flips the direction. */
  onSort: (key: SearchCandidateSortKey) => void;
  /** Shown in place of rows — the caller words it for the reason it is empty. */
  emptyMessage: string;
  /** "Now" for the In stage column; injectable so a test can pin a number. */
  now?: Date;
  /** Thrive contact id (as a string) → photo URL, for the rows that have one.
   *  Absent → the monogram. Resolved by `useResolveThriveAvatars`. */
  avatarUrls?: ReadonlyMap<string, string>;
  /** The stored-report reads for the Reports column. Absent → em dashes. */
  reports?: { client: CandidateReportsClient; searchId: string };
  /** Open the candidate panel on a row (its candidacy id). Absent → rows are
   *  not clickable and the name is plain text. */
  onOpenCandidate?: (candidacyId: string) => void;
}

/**
 * A click on the row that should open the panel: not on a control of its own
 * (a link, a button, a menu entry), and not from a PORTAL — React bubbles a
 * click inside a portalled Radix menu through the component tree to this row,
 * though the menu is nowhere inside it in the DOM.
 */
function opensPanel(e: MouseEvent<HTMLTableRowElement>): boolean {
  const target = e.target;
  if (!(target instanceof Element) || !e.currentTarget.contains(target)) return false;
  return !target.closest("a, button, input, select, label, [role='menuitem']");
}

/** The sort control alone — a header renders one, or two side by side. */
function SortControl({
  label,
  columnKey,
  sort,
  order,
  onSort,
  testId,
}: {
  label: string;
  columnKey: SearchCandidateSortKey;
  sort: SearchCandidateSortKey;
  order: "asc" | "desc";
  onSort: (key: SearchCandidateSortKey) => void;
  testId: string;
}) {
  const active = sort === columnKey;
  return (
    <button
      type="button"
      data-testid={`${testId}-sort`}
      data-active={active ? "true" : undefined}
      onClick={() => onSort(columnKey)}
      className={`inline-flex items-center gap-1 hover:text-text ${active ? "text-accent" : ""}`}
    >
      {label}
      {/* Always show an affordance so the feature is discoverable — a dim
          double arrow when inactive, the bright active direction when not. */}
      <span aria-hidden="true" className={active ? "text-accent" : "text-text-muted"}>
        {active ? (order === "desc" ? "▼" : "▲") : "↕"}
      </span>
    </button>
  );
}

/** `Austin, TX` / `Austin` / `TX`; `null` when the contact has neither. */
export function formatLocation(city: string | null | undefined, state: string | null | undefined) {
  const parts = [city, state].filter((part): part is string => Boolean(part?.trim()));
  return parts.length === 0 ? null : parts.join(", ");
}

/** `VP Product · Acme Corp` / `VP Product` / `Acme Corp`; `null` for neither —
 *  never a dangling separator. */
export function formatTitleAndCompany(
  title: string | null | undefined,
  company: string | null | undefined
) {
  const parts = [title, company].filter((part): part is string => Boolean(part?.trim()));
  return parts.length === 0 ? null : parts.join(" · ");
}

function SortableHeader({
  label,
  columnKey,
  sort,
  order,
  onSort,
  testId,
}: {
  label: string;
  columnKey: SearchCandidateSortKey;
  sort: SearchCandidateSortKey;
  order: "asc" | "desc";
  onSort: (key: SearchCandidateSortKey) => void;
  testId: string;
}) {
  const active = sort === columnKey;
  return (
    <th
      scope="col"
      data-testid={`${testId}-header`}
      // Sortable-but-inactive columns announce "none" so a screen reader knows
      // they can be sorted at all.
      aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}
      className="sticky top-0 z-10 whitespace-nowrap bg-table-cell-surface px-3 py-2 text-left"
    >
      <SortControl
        label={label}
        columnKey={columnKey}
        sort={sort}
        order={order}
        onSort={onSort}
        testId={testId}
      />
    </th>
  );
}

/**
 * The Candidate header: THREE controls, one per line of the cell it heads —
 * the name, the title, the company — each naming what it orders. The cell
 * announces the direction of whichever is active.
 */
function CandidateHeader({
  sort,
  order,
  onSort,
}: {
  sort: SearchCandidateSortKey;
  order: "asc" | "desc";
  onSort: (key: SearchCandidateSortKey) => void;
}) {
  const active = sort === "name" || sort === "title" || sort === "company";
  return (
    <th
      scope="col"
      data-testid="candidates-name-header"
      aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}
      className="sticky top-0 z-10 whitespace-nowrap bg-table-cell-surface px-3 py-2 text-left"
    >
      <span className="inline-flex items-center gap-1">
        <SortControl
          label="Candidate"
          columnKey="name"
          sort={sort}
          order={order}
          onSort={onSort}
          testId="candidates-name"
        />
        <span aria-hidden="true" className="text-text-secondary">
          ·
        </span>
        <SortControl
          label="Title"
          columnKey="title"
          sort={sort}
          order={order}
          onSort={onSort}
          testId="candidates-title"
        />
        <span aria-hidden="true" className="text-text-secondary">
          ·
        </span>
        <SortControl
          label="company"
          columnKey="company"
          sort={sort}
          order={order}
          onSort={onSort}
          testId="candidates-company"
        />
      </span>
    </th>
  );
}

/** The headers above, counted — the empty row spans them all. A unit test
 *  compares it with the rendered header cells. */
const COLUMN_COUNT = 6;

export function CandidatesTable({
  rows,
  sort,
  order,
  onSort,
  emptyMessage,
  now = new Date(),
  avatarUrls,
  reports,
  onOpenCandidate,
}: CandidatesTableProps) {
  return (
    // `max-h` + `overflow-auto`: the table scrolls inside its own container, so
    // 50 rows of long company names never push the page body sideways.
    // `bg-table-surface` on the wrapper (SOP frontend-design-system
    // 09-pitfalls §7): this table sits in a bare column, not inside a
    // `bg-surface` Panel like its Dashboard predecessor, so without the sheet
    // the rows rested on the page while the sticky header painted white.
    // `min-w-[56rem]` (896 px; it was 72rem with nine columns): at a 1280 px
    // window the table's room is 1280 − 256 (the expanded left menu) − 64 (the
    // page's `p-8`) − 2 (the border) = 958 px, less a classic scrollbar, so
    // the six columns fit and Reports shows without a horizontal scroll.
    <div
      data-testid="candidates-scroller"
      className="max-h-[40rem] overflow-auto rounded-sm border border-border bg-table-surface"
    >
      <table className="w-full min-w-[56rem] border-collapse text-caption">
        <thead className="border-b border-border text-text-secondary">
          <tr>
            <CandidateHeader sort={sort} order={order} onSort={onSort} />
            {/* Not sortable — the one stated exception; see the module note. */}
            <th
              scope="col"
              data-testid="candidates-thrive-header"
              className="sticky top-0 z-10 whitespace-nowrap bg-table-cell-surface px-3 py-2 text-left"
            >
              Thrive
            </th>
            {/* The CURRENT stage; the highest rides under it in the cell. */}
            <SortableHeader
              label="Stage"
              columnKey="stage"
              sort={sort}
              order={order}
              onSort={onSort}
              testId="candidates-stage"
            />
            <SortableHeader
              label="In stage"
              columnKey="stage_since"
              sort={sort}
              order={order}
              onSort={onSort}
              testId="candidates-in-stage"
            />
            <SortableHeader
              label="Added"
              columnKey="created_at"
              sort={sort}
              order={order}
              onSort={onSort}
              testId="candidates-added"
            />
            {/* Not sortable: two buttons per row order nothing. */}
            <th
              scope="col"
              data-testid="candidates-reports-header"
              className="sticky top-0 z-10 whitespace-nowrap bg-table-cell-surface px-3 py-2 text-left"
            >
              Reports
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={COLUMN_COUNT}
                data-testid="candidates-empty"
                className="px-3 py-6 text-center text-body text-text-secondary"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const titleAndCompany = formatTitleAndCompany(row.title, row.company);
              const showHighest = Boolean(row.highest_stage) && row.highest_stage !== row.stage;
              const rejected = row.stage === REJECTED_STAGE;
              const contactId =
                row.contact_id === null || row.contact_id === undefined
                  ? null
                  : String(row.contact_id);
              const candidacyId =
                row.candidacy_id === null || row.candidacy_id === undefined
                  ? null
                  : String(row.candidacy_id);
              const openable = Boolean(onOpenCandidate && candidacyId);
              const nameText = (
                <span data-testid="candidate-name" className="text-text">
                  {row.name ?? "—"}
                </span>
              );
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: the row click is a pointer shortcut onto the name's button, which is the keyboard path.
                <tr
                  key={row.candidacy_id ?? `${row.name}-${row.email}`}
                  data-testid="candidates-row"
                  // The candidacy id, so a spec addresses a row by identity
                  // rather than by position (e2e-expert checklist).
                  data-candidacy-id={row.candidacy_id ?? undefined}
                  // A pointer shortcut onto the name's button, which is the
                  // keyboard path (and where focus returns when the panel
                  // closes), so the row itself needs no key handler.
                  onClick={
                    openable
                      ? (e) => {
                          if (opensPanel(e) && candidacyId) onOpenCandidate?.(candidacyId);
                        }
                      : undefined
                  }
                  className={`border-b border-border last:border-b-0 hover:bg-row-hover ${
                    openable ? "cursor-pointer" : ""
                  }`}
                >
                  {/* `min-w-64` (2026-10-07): without a floor the narrow column
                      wrapped the name onto several lines. */}
                  <td className="min-w-64 px-3 py-2 align-top">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        {/* Initials until a photo resolves — `src` is undefined
                            while the store is asked and for a contact with no
                            photo anywhere; a URL that fails to load falls back
                            to the monogram inside the primitive. A fixed slot
                            either way, so the row does not reflow when the
                            photo lands. `shrink-0` on this WRAPPER, the flex
                            item: without it the column squeezed the photo
                            into a vertical oval, thinner the longer the name
                            (Jose, 2026-10-07). */}
                        <span data-testid="candidate-avatar" className="shrink-0">
                          <Avatar
                            src={contactId ? avatarUrls?.get(contactId) : undefined}
                            name={row.name}
                            size="lg"
                          />
                        </span>
                        <span className="flex min-w-0 flex-col">
                          {openable && candidacyId ? (
                            <button
                              type="button"
                              data-testid="candidate-open"
                              onClick={() => onOpenCandidate?.(candidacyId)}
                              aria-label={`Open ${row.name ?? "this candidate"}’s reports`}
                              className="self-start text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                              {nameText}
                            </button>
                          ) : (
                            nameText
                          )}
                          <span
                            data-testid="candidate-title-company"
                            className="max-w-60 truncate text-caption text-text-secondary"
                            title={titleAndCompany ?? undefined}
                          >
                            {titleAndCompany ?? "—"}
                          </span>
                          {row.email ? (
                            <a
                              href={`mailto:${row.email}`}
                              data-testid="candidate-email-link"
                              className="inline-flex max-w-60 items-center gap-1 self-start text-caption text-accent-ink hover:underline"
                              title={row.email}
                            >
                              <Mail className="h-3 w-3 shrink-0" aria-hidden="true" />
                              <span className="truncate">{row.email}</span>
                            </a>
                          ) : (
                            <span data-testid="candidate-email-missing" className="text-text-muted">
                              —
                            </span>
                          )}
                        </span>
                      </div>
                      {row.linkedin_url ? (
                        <a
                          href={row.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="candidate-linkedin-link"
                          aria-label={`Open ${row.name ?? "this candidate"} on LinkedIn`}
                          title="Open LinkedIn profile"
                          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-accent-ink hover:bg-accent-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                          <Linkedin aria-hidden="true" className="h-4 w-4" />
                        </a>
                      ) : null}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 align-top tabular-nums">
                    {contactId === null ? (
                      // Null for a candidacy Thrive sent without a `candidate`
                      // relationship, and for any row cached before the field
                      // existed (2026-08-27) — both resolve on the next
                      // recompose. The name still renders; a missing id never
                      // costs the row.
                      <span className="text-text-muted">—</span>
                    ) : (
                      <ThriveIdCell
                        contactId={contactId}
                        showAvatar={false}
                        cellTestId="candidate-thrive-cell"
                        linkTestId="candidate-thrive-id"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <span className="flex flex-col items-start gap-1">
                      <StagePill stage={row.stage} />
                      {showHighest ? (
                        <span
                          data-testid="candidate-highest-stage"
                          className="whitespace-nowrap text-caption text-text-secondary"
                        >
                          <span aria-hidden="true">↑ </span>
                          <span className="sr-only">Highest stage: </span>
                          {row.highest_stage}
                        </span>
                      ) : null}
                      {rejected && row.rejection_reason ? (
                        <span
                          data-testid="candidate-rejection-reason"
                          className="max-w-[16rem] text-caption text-text-secondary"
                        >
                          {row.rejection_reason}
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td
                    data-testid="candidate-in-stage"
                    className="whitespace-nowrap px-3 py-2 align-top tabular-nums"
                  >
                    {formatDaysInStage(row.stage_since, now)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 align-top">
                    {formatDate(row.created_at)}
                  </td>
                  <td data-testid="candidate-reports" className="px-3 py-2 align-top">
                    {reports ? (
                      <CandidateReportButtons
                        row={row}
                        client={reports.client}
                        searchId={reports.searchId}
                      />
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
